import { useCallback, useEffect, useRef, useState } from "react";

// Perpendicular distance from `p` to the line segment (p1, p2) in lat/lng
// degree space. Used by Douglas-Peucker simplification. Not metric-accurate
// (degrees aren't equal on the surface), but the polygon's simplification
// only needs internally consistent comparisons.
const perpendicularDistance = (p, p1, p2) => {
    const dx = p2.lng - p1.lng;
    const dy = p2.lat - p1.lat;
    if (dx === 0 && dy === 0) {
        const ddx = p.lng - p1.lng;
        const ddy = p.lat - p1.lat;
        return Math.sqrt(ddx * ddx + ddy * ddy);
    }
    const t = ((p.lng - p1.lng) * dx + (p.lat - p1.lat) * dy) / (dx * dx + dy * dy);
    const clampedT = Math.max(0, Math.min(1, t));
    const projx = p1.lng + clampedT * dx;
    const projy = p1.lat + clampedT * dy;
    const ddx = p.lng - projx;
    const ddy = p.lat - projy;
    return Math.sqrt(ddx * ddx + ddy * ddy);
};

// Douglas-Peucker. Recursively keeps the vertex with the largest perpendicular
// distance to the chord (start, end) if that distance exceeds `tol`. Without
// this a freehand drag produces 200-500 raw mousemove samples; the output
// here lands in the 10-50 range for any reasonable shape.
const dpRec = (pts, start, end, tol, out) => {
    let maxDist = 0;
    let maxIdx = -1;
    for (let i = start + 1; i < end; i++) {
        const d = perpendicularDistance(pts[i], pts[start], pts[end]);
        if (d > maxDist) {
            maxDist = d;
            maxIdx = i;
        }
    }
    if (maxDist > tol && maxIdx >= 0) {
        dpRec(pts, start, maxIdx, tol, out);
        out.push(pts[maxIdx]);
        dpRec(pts, maxIdx, end, tol, out);
    }
};

const simplifyPath = (pts, tol) => {
    if (pts.length < 3) return pts;
    const out = [pts[0]];
    dpRec(pts, 0, pts.length - 1, tol, out);
    out.push(pts[pts.length - 1]);
    return out;
};

// Tolerance auto-scales with the polygon's bbox so a city-scale draw and a
// neighborhood-scale draw both end up with a similar vertex count after
// simplification. ~0.5% of the bbox diagonal gives smooth shapes that
// still preserve user intent. Clamped so degenerate cases (tiny click-
// blip drag) don't try to simplify with a near-zero tolerance.
const computeTolerance = (pts) => {
    let minLat = pts[0].lat;
    let maxLat = pts[0].lat;
    let minLng = pts[0].lng;
    let maxLng = pts[0].lng;
    for (const p of pts) {
        if (p.lat < minLat) minLat = p.lat;
        if (p.lat > maxLat) maxLat = p.lat;
        if (p.lng < minLng) minLng = p.lng;
        if (p.lng > maxLng) maxLng = p.lng;
    }
    const diag = Math.hypot(maxLat - minLat, maxLng - minLng);
    return Math.max(0.0001, Math.min(0.01, diag * 0.005));
};

// Hook driving the manual freehand polygon draw mode (P6.5/P6.6).
//
// Returns: { isDrawing, isPressed, vertices, start(), cancel(), finish() }
//
// Behavior while isDrawing is true:
//   - mousedown on the map clears any previous path and starts a fresh one
//     (so the user can iterate — draw, look, redraw — without going through
//     Cancelar). isPressed flips true.
//   - mousemove WHILE PRESSED accumulates vertices, distance-throttled so a
//     slow careful drag doesn't fill the array with sub-pixel samples.
//   - mouseup just flips isPressed false. **It does NOT commit.** The drawn
//     path stays visible as a preview polygon until the user clicks Aplicar
//     (which calls finish() → DP simplify → onFinish) or Cancelar (which
//     calls cancel()). Matches Zillow's two-step UX: drag to draw, then
//     explicit confirm. Better than auto-commit on mouseup because the user
//     can preview the shape before committing, and re-drag to redraw.
//   - Esc cancels at any time (vertices cleared, isDrawing=false).
//
// During draw mode, `gestureHandling: 'none'` and `draggableCursor:
// 'crosshair'` are set on the map so the freehand drag doesn't pan the map
// and the cursor signals draw intent. Both are restored on exit.
//
// Click-to-place-vertex (P6.5's original spec) was rejected after observing
// Zillow's actual UX live (single click on the map in draw mode is a no-op
// for them) — freehand drag is the only way to draw.
//
// Needs the google.maps.Map instance via `mapRef` (the same ref MapPane's
// CaptureMapRef writes into for the rest of the layout's imperative camera
// control).
const useDrawPolygon = ({ mapRef, onFinish }) => {
    const [isDrawing, setIsDrawing] = useState(false);
    const [isPressed, setIsPressed] = useState(false);
    const [vertices, setVertices] = useState([]);
    // Mirror state into refs so the listeners (which close over their
    // initial values) can always read the latest sync state.
    const verticesRef = useRef([]);
    verticesRef.current = vertices;
    const isPressedRef = useRef(false);
    // Snap state lives in a ref because the mousemove listener mutates it
    // synchronously and doesn't need to trigger re-renders for state
    // updates the user can't perceive directly. Resets on mousedown
    // (every new drag starts un-snapped).
    const hasSnappedRef = useRef(false);

    const start = useCallback(() => {
        verticesRef.current = [];
        isPressedRef.current = false;
        setVertices([]);
        setIsPressed(false);
        setIsDrawing(true);
    }, []);

    const cancel = useCallback(() => {
        verticesRef.current = [];
        isPressedRef.current = false;
        setVertices([]);
        setIsPressed(false);
        setIsDrawing(false);
    }, []);

    // External finish — wired to the Aplicar banner button. Reads the
    // accumulated vertices (from the last drag), simplifies, commits.
    const finish = useCallback(() => {
        const raw = verticesRef.current;
        if (raw.length >= 3) {
            const simplified = simplifyPath(raw, computeTolerance(raw));
            if (simplified.length >= 3) onFinish?.(simplified);
        }
        verticesRef.current = [];
        isPressedRef.current = false;
        setVertices([]);
        setIsPressed(false);
        setIsDrawing(false);
    }, [onFinish]);

    // Pointer listeners (mouse + touch) + gesture/cursor lockdown for the
    // duration of draw mode. Google Maps' addListener('mousedown'/'mousemove'
    // /'mouseup', ...) catches the mouse path; touch needs DOM-level
    // listeners on the map's container because Google's listener layer
    // doesn't bridge touch into mouse events (it routes taps to `click`
    // and drags to `drag*` events, which we've already suppressed via
    // gestureHandling:'none').
    useEffect(() => {
        const map = mapRef?.current;
        if (!isDrawing || !map) return;
        if (typeof window === "undefined" || !window.google?.maps) return;

        const prevGesture = map.get("gestureHandling");
        const prevCursor = map.get("draggableCursor");
        map.setOptions({
            gestureHandling: "none",
            draggableCursor: "crosshair",
        });

        // ~11m at the equator. Below this distance a sample is visual noise
        // and gets discarded (cheaper than DP smoothing it out later). The
        // DP simplification on Aplicar is the real cleanup.
        const SAMPLE_THRESHOLD_DEG = 0.0001;
        const thresholdSq = SAMPLE_THRESHOLD_DEG * SAMPLE_THRESHOLD_DEG;

        // Start-vertex snap radii (in screen pixels). Cursor within
        // SNAP_PX_IN of vertex[0] → path's last vertex locks to vertex[0]
        // (magnetic close). Sticky with hysteresis — once snapped, must
        // move beyond SNAP_PX_OUT to break free. Catches the "missed the
        // start, kept fumbling" tangle case. ≥4 vertices required before
        // snap activates so a tiny drag near start doesn't insta-close.
        const SNAP_PX_IN = 20;
        const SNAP_PX_OUT = 40;
        const SNAP_MIN_VERTICES = 4;

        // Project container pixel coords back to lat/lng. Touch events give
        // us clientX/clientY (DOM pixels); Google Maps' built-in projection
        // converts via the world-coord system (256x256 at zoom 0). For
        // mouse-path events we get latLng directly from the MapMouseEvent
        // and skip this.
        const containerPxToLatLng = (containerX, containerY) => {
            const projection = map.getProjection();
            const bounds = map.getBounds();
            const div = map.getDiv?.();
            if (!projection || !bounds || !div) return null;
            const ne = projection.fromLatLngToPoint(bounds.getNorthEast());
            const sw = projection.fromLatLngToPoint(bounds.getSouthWest());
            const fx = containerX / div.clientWidth;
            const fy = containerY / div.clientHeight;
            const worldX = sw.x + fx * (ne.x - sw.x);
            // Y increases southward in Mercator world coords, so NE has
            // smaller Y (top) and SW has larger Y (bottom).
            const worldY = ne.y + fy * (sw.y - ne.y);
            return projection.fromPointToLatLng(new window.google.maps.Point(worldX, worldY));
        };

        // Shared input-path helpers — both mouse and touch handlers call
        // these so the snap+accumulate logic only lives in one place.
        const handleDragStart = (lat, lng) => {
            isPressedRef.current = true;
            setIsPressed(true);
            hasSnappedRef.current = false;
            // Replace any previous path — pointerdown after a finished drag
            // means "I want to redraw," not "I want to extend."
            const point = { lat, lng };
            verticesRef.current = [point];
            setVertices([point]);
        };
        const handleDragMove = (cursorLat, cursorLng) => {
            if (!isPressedRef.current) return;
            // Magnetic snap to the start vertex when close enough. Pixel-
            // based so the snap zone stays consistent across zoom levels.
            const verts = verticesRef.current;
            if (verts.length >= SNAP_MIN_VERTICES) {
                const start = verts[0];
                const zoom = map.getZoom();
                // Web Mercator: pixels-per-degree at zoom Z is linear in
                // lng (256 * 2^Z / 360) and 1/cos(lat) for lat at lat L.
                const pxPerDeg = (256 * Math.pow(2, zoom)) / 360;
                const pxPerDegLat = pxPerDeg / Math.cos((start.lat * Math.PI) / 180);
                const dxPx = (cursorLng - start.lng) * pxPerDeg;
                const dyPx = (cursorLat - start.lat) * pxPerDegLat;
                const distSqPx = dxPx * dxPx + dyPx * dyPx;
                const threshold = hasSnappedRef.current ? SNAP_PX_OUT : SNAP_PX_IN;
                if (distSqPx < threshold * threshold) {
                    hasSnappedRef.current = true;
                    const last = verts[verts.length - 1];
                    if (last.lat === start.lat && last.lng === start.lng) return;
                    const next = [...verts.slice(0, -1), start];
                    verticesRef.current = next;
                    setVertices(next);
                    return;
                }
                if (hasSnappedRef.current) {
                    hasSnappedRef.current = false;
                    const next = [...verts.slice(0, -1), { lat: cursorLat, lng: cursorLng }];
                    verticesRef.current = next;
                    setVertices(next);
                    return;
                }
            }
            // Outside snap zone — normal distance-throttled accumulate.
            const last = verts[verts.length - 1];
            const dLat = cursorLat - last.lat;
            const dLng = cursorLng - last.lng;
            if (dLat * dLat + dLng * dLng < thresholdSq) return;
            const point = { lat: cursorLat, lng: cursorLng };
            const next = [...verts, point];
            verticesRef.current = next;
            setVertices(next);
        };
        const handleDragEnd = () => {
            if (!isPressedRef.current) return;
            isPressedRef.current = false;
            setIsPressed(false);
        };

        // Mouse path — Google Maps fires these via addListener and gives
        // us latLng directly on the event.
        const onMouseDown = (e) => {
            if (!e.latLng) return;
            handleDragStart(e.latLng.lat(), e.latLng.lng());
        };
        const onMouseMove = (e) => {
            if (!e.latLng) return;
            handleDragMove(e.latLng.lat(), e.latLng.lng());
        };
        const onMouseUp = () => handleDragEnd();

        // Touch path — DOM listeners on the map container. preventDefault
        // suppresses both the browser's default touch behaviors (scroll/
        // zoom) AND the synthetic mouse events that browsers fire as
        // touch fallbacks (so the mouse path doesn't double-fire on
        // touch devices).
        const mapDiv = map.getDiv?.();
        const extractTouch = (e) => {
            const t = e.touches[0] || e.changedTouches?.[0];
            if (!t || !mapDiv) return null;
            const rect = mapDiv.getBoundingClientRect();
            return containerPxToLatLng(t.clientX - rect.left, t.clientY - rect.top);
        };
        const onTouchStart = (e) => {
            e.preventDefault();
            const ll = extractTouch(e);
            if (!ll) return;
            handleDragStart(ll.lat(), ll.lng());
        };
        const onTouchMove = (e) => {
            if (!isPressedRef.current) return;
            e.preventDefault();
            const ll = extractTouch(e);
            if (!ll) return;
            handleDragMove(ll.lat(), ll.lng());
        };
        const onTouchEnd = (e) => {
            if (!isPressedRef.current) return;
            e.preventDefault();
            handleDragEnd();
        };

        const downL = map.addListener("mousedown", onMouseDown);
        const moveL = map.addListener("mousemove", onMouseMove);
        const upL = map.addListener("mouseup", onMouseUp);

        // Touch listeners go on the map's container div. passive:false is
        // required to call preventDefault on touchstart/touchmove (which
        // we need to suppress the browser's default scroll/zoom AND the
        // synthetic mouse fallback events).
        if (mapDiv) {
            mapDiv.addEventListener("touchstart", onTouchStart, { passive: false });
            mapDiv.addEventListener("touchmove", onTouchMove, { passive: false });
            mapDiv.addEventListener("touchend", onTouchEnd);
            mapDiv.addEventListener("touchcancel", onTouchEnd);
        }

        return () => {
            downL.remove();
            moveL.remove();
            upL.remove();
            if (mapDiv) {
                mapDiv.removeEventListener("touchstart", onTouchStart);
                mapDiv.removeEventListener("touchmove", onTouchMove);
                mapDiv.removeEventListener("touchend", onTouchEnd);
                mapDiv.removeEventListener("touchcancel", onTouchEnd);
            }
            map.setOptions({
                gestureHandling: prevGesture ?? "auto",
                draggableCursor: prevCursor ?? null,
            });
        };
    }, [isDrawing, mapRef]);

    // Esc to cancel. Listener attached only while drawing.
    useEffect(() => {
        if (!isDrawing) return;
        const onKey = (e) => {
            if (e.key === "Escape") cancel();
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [isDrawing, cancel]);

    return { isDrawing, isPressed, vertices, start, cancel, finish };
};

export default useDrawPolygon;
